import React, { useState, KeyboardEvent } from "react";
import {
    Box,
    Input,
    Tag,
    TagLabel,
    TagCloseButton,
    Wrap,
    WrapItem,
    Text,
    InputGroup,
    VStack,
} from "@chakra-ui/react";
import { useIntl } from "react-intl";

interface MultiEmailInputProps {
    emails: string[];
    onEmailsChange: (emails: string[]) => void;
    placeholder?: string;
    isDisabled?: boolean;
}

const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
    emails,
    onEmailsChange,
    placeholder,
    isDisabled = false
}) => {
    const intl = useIntl();
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState("");

    const addEmail = () => {
        if (isDisabled) return;
        
        const trimmedEmail = inputValue.trim();
        
        if (!trimmedEmail) return;
        
        if (!isValidEmail(trimmedEmail)) {
            setError(intl.formatMessage({ id: "invalidEmail" }));
            return;
        }
        
        if (emails.includes(trimmedEmail)) {
            setError(intl.formatMessage({ id: "emailAlreadyAdded" }));
            return;
        }
        
        onEmailsChange([...emails, trimmedEmail]);
        setInputValue("");
        setError("");
    };

    const removeEmail = (emailToRemove: string) => {
        if (isDisabled) return;
        onEmailsChange(emails.filter(email => email !== emailToRemove));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (isDisabled) return;
        
        if (e.key === "Enter") {
            e.preventDefault();
            addEmail();
        } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
            // Remove last email when backspace is pressed on empty input
            removeEmail(emails[emails.length - 1]);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isDisabled) return;
        setInputValue(e.target.value);
        if (error) setError(""); // Clear error when user starts typing
    };

    return (
        <VStack align="stretch" spacing={2}>
            <Box
                border="1px solid"
                borderColor={isDisabled ? "gray.100" : "gray.200"}
                borderRadius="md"
                p={2}
                minH="40px"
                bg={isDisabled ? "gray.50" : "white"}
                opacity={isDisabled ? 0.6 : 1}
                _focusWithin={!isDisabled ? {
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px #2895ff"
                } : {}}
            >
                <Wrap spacing={1} mb={emails.length > 0 ? 2 : 0}>
                    {emails.map((email, index) => (
                        <WrapItem key={index}>
                            <Tag
                                size="sm"
                                variant="solid"
                                bg="brand.500"
                                color="white"
                                borderRadius="full"
                            >
                                <TagLabel fontSize="xs">{email}</TagLabel>
                                <TagCloseButton
                                    onClick={() => removeEmail(email)}
                                    _hover={{ bg: "brand.600" }}
                                    isDisabled={isDisabled}
                                />
                            </Tag>
                        </WrapItem>
                    ))}
                </Wrap>
                <InputGroup>
                    <Input
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder || intl.formatMessage({ id: "email" })}
                        border="none"
                        p={0}
                        fontSize="sm"
                        _focus={{ boxShadow: "none" }}
                        _placeholder={{ color: "gray.400" }}
                        isDisabled={isDisabled}
                        cursor={isDisabled ? "not-allowed" : "text"}
                    />
                </InputGroup>
            </Box>
            {error && !isDisabled && (
                <Text color="red.500" fontSize="xs">
                    {error}
                </Text>
            )}
            {!isDisabled && (
                <Text fontSize="xs" color="gray.500">
                    {intl.formatMessage({ id: "pressEnterToAddEmail" })}
                </Text>
            )}
        </VStack>
    );
};

export default MultiEmailInput; 